import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { useApi } from "../api-context";
import { useDesktopUpdates } from "../desktop-updates";
import {
  headerConnectionNudgeHref,
  headerConnectionNudgeLabel,
  persistHeaderConnectionNudgeDismissed,
  readHeaderConnectionNudgeDismissed,
  shouldShowHeaderConnectionNudge,
} from "../header-connection-nudge";
import {
  readThisComputerViewerOnly,
  thisComputerViewerOnlyQueryKey,
} from "../this-computer-placeholder";

export function HeaderConnectionNudge({
  organizationId,
}: {
  readonly organizationId: string;
}) {
  const api = useApi();
  const { desktop, enrolled, status } = useDesktopUpdates();
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const connections = useQuery({
    queryKey: ["connections", organizationId],
    queryFn: () => api.connections(organizationId),
    refetchInterval: 60_000,
  });
  const currentPrincipal = useQuery({
    queryKey: ["current-principal"],
    queryFn: () => api.currentPrincipal(),
  });
  const principalId = currentPrincipal.data?.principal_id;
  const dismissed =
    sessionDismissed ||
    (principalId !== undefined &&
      readHeaderConnectionNudgeDismissed(organizationId, principalId));
  const viewerOnlyQuery = useQuery({
    queryKey: thisComputerViewerOnlyQueryKey(organizationId, principalId ?? ""),
    queryFn: () =>
      principalId === undefined
        ? false
        : readThisComputerViewerOnly(organizationId, principalId),
    enabled: principalId !== undefined,
  });

  if (desktop && status.isPending) {
    return null;
  }
  if (
    principalId === undefined ||
    connections.data === undefined ||
    !shouldShowHeaderConnectionNudge({
      connections: connections.data.connections,
      principalId,
      dismissed,
      desktopRuntime: desktop,
      enrolled,
      viewerOnly: viewerOnlyQuery.data === true,
    })
  ) {
    return null;
  }

  const label = headerConnectionNudgeLabel(desktop);

  return (
    <div className="header-connection-nudge" role="status">
      <p className="header-connection-nudge__label">{label}</p>
      <Link
        className="header-connection-nudge__action"
        to={headerConnectionNudgeHref(organizationId, desktop)}
        aria-label="Open connections"
      >
        Open
      </Link>
      <button
        className="header-connection-nudge__action"
        type="button"
        onClick={() => {
          persistHeaderConnectionNudgeDismissed(organizationId, principalId);
          setSessionDismissed(true);
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
